import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Row,
  Col,
  Radio,
  Checkbox,
  Modal,
  Dropdown,
  Typography,
  Space,
  message,
  Spin,
  Divider,
  Tag,
  Badge,
  Tooltip,
  Drawer
} from "antd";
import CartContext from "../../../context/CartContext";
import { BusinessContext } from "../../../context/BusinessContext";
import { fetchCategoriesAndFoodItems, fetchFoodCustomization } from "../../../utils/api";
import {
  MenuOutlined,
  ShoppingCartOutlined,
  PlusOutlined,
  DownOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  StarFilled,
  FireOutlined,
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";
import "../../../assets/css/Menu.css";
import "./foodItems.css";

const { Title, Text } = Typography;

const FoodItems = () => {
  // State declarations
  const [sizes, setSizes] = useState([]);
  const [toppings, setToppings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const { dispatch, addItem } = useContext(CartContext);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isMobile, setIsMobile] = useState(false);
  const sectionRefs = useRef([]);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [loadingItems, setLoadingItems] = useState({});
  const [Menuloading, setMenuLoading] = useState(false);
  const { businessInfo } = useContext(BusinessContext);
  const comprisionwebs = businessInfo?.businessInfo?.[0]?.comprisionweb || [];
  const categoriesScrollRef = useRef(null);

  const restaurantInfo = businessInfo?.businessInfo?.[0];

const isRestaurantMenuClosed =
  Number(restaurantInfo?.portal_info?.[0]?.res_direct_close) === 1 || 
  Number(restaurantInfo?.portal_info?.[0]?.res_direct_order_disable) === 1;


  // Calculate total price function
  const calculateTotalPrice = () => {
    let total = 0;
    
    // Add selected size price
    if (selectedSize) {
      total += parseFloat(
        selectedSize.delivery_discount > 0 
          ? selectedSize.delivery_discount 
          : selectedSize.delivery_price
      );
    }
    
    // Add selected toppings prices
    toppings
      .filter(t => selectedToppings.includes(t.id))
      .forEach(t => {
        total += parseFloat(t.delivery_price);
      });
    
    return total.toFixed(2);
  };

  // Handle Add to Cart
  const handleAddToCart = async (item, UberEatsComparisonRate) => {

    if (isRestaurantMenuClosed) {
    message.warning("The restaurant is currently closed.");
    return;
  }
    setLoadingItems((prev) => ({ ...prev, [item.id]: true })); 
    try {
      const orderType = localStorage.getItem("order_type") || 0;
      await addItem(item, orderType, UberEatsComparisonRate); 
      message.success(`${item.food_item_name_en} added to cart!`);
    } catch (error) {
      message.error(`Failed to add ${item.food_item_name_en} to cart. Try again!`);
    } finally {
      setLoadingItems((prev) => ({ ...prev, [item.id]: false })); 
    }
  };

  // Handle Customize
  const handleCustomize = async (item) => {
    setSelectedItem(item);
    setIsDrawerVisible(true);
    setLoading(true);

    try {
      const data = await fetchFoodCustomization(item.id);
      if (data) {
        setSizes(data.sizes);
        setToppings(data.addons);
        // Set the first size as default selection
        if (data.sizes && data.sizes.length > 0) {
          setSelectedSize(data.sizes[0]);
        }
      }
    } catch (err) {
      console.error("Error fetching customization:", err);
      message.error("Failed to load customization options");
    } finally {
      setLoading(false);
    }
  };

  // Handle OK from drawer
const handleOk = async () => {
  if (!selectedSize) {
    message.warning("Please select a size");
    return;
  }

  const customizedItem = {
    ...selectedItem,
    size_id: selectedSize.id,              // send size ID
    size_name: selectedSize.name_en,       // keep name for frontend
    extra_id: selectedToppings, 
    size: selectedSize,
    toppings: selectedToppings,
    finalPrice: calculateTotalPrice()
  };

  const orderType = localStorage.getItem("order_type") || 0;

  console.log("API Payload (customized item):", { customizedItem, orderType, UberEatsComparisonRate });

  try {
    await addItem(customizedItem, orderType, UberEatsComparisonRate);
    message.success(
      `${selectedItem.food_item_name_en} (${selectedSize.name_en}) added to cart!`
    );
    setIsDrawerVisible(false);
    resetDrawer();
  } catch (error) {
    message.error(`Failed to add ${selectedItem.food_item_name_en} to cart. Try again!`);
  }
};


  // Handle Cancel from drawer
  const handleCancel = () => {
    setIsDrawerVisible(false);
    resetDrawer();
  };

  // Reset drawer state
  const resetDrawer = () => {
    setSelectedItem(null);
    setSelectedSize(null);
    setSelectedToppings([]);
  };

  // Fetch menu data
  const getCategoriesAndFoodItems = async () => {
    setMenuLoading(true);
    try {
      const fetchedData = await fetchCategoriesAndFoodItems();
      if (fetchedData) {
        setMenuData(fetchedData.data || []);
      } else {
        console.error("Failed to fetch categories and food items");
      }
    } catch (error) {
      console.error("Error fetching categories and food items:", error);
      message.error("Failed to load menu");
    } finally {
      setMenuLoading(false);
    }
  };

  // Handle category click
  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    const categoryElement = document.getElementById(categoryId);
    if (categoryElement) {
      categoryElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Scroll categories horizontally
  const scrollCategories = (direction) => {
    if (categoriesScrollRef.current) {
      const scrollAmount = 200;
      categoriesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    getCategoriesAndFoodItems();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSelectedCategory(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => {
      window.removeEventListener("resize", handleResize);
      sectionRefs.current.forEach((ref) => ref && observer.unobserve(ref));
    };
  }, []);

  // Calculate UberEats comparison rate
let UberEatsComparisonRate = 0;
const companyData = comprisionwebs.find(
  (comprisionweb) =>
    ["Uber Eats", "Deliveroo", "Just Eat"].includes(comprisionweb.company_name)
);
if (companyData) {
  UberEatsComparisonRate = parseFloat(companyData.price_discount) / 100 || 0;
}

  // Category menu for dropdown
  const categoryMenu = (
    <div className="category-dropdown-menu">
      {menuData.map((category) => (
        <Button
          key={category.id}
          className={`category-dropdown-btn ${
            selectedCategory === category.id ? "active" : ""
          }`}
          onClick={() => handleCategoryClick(category.id)}
        >
          {category.menu_cat_name_en}
        </Button>
      ))}
    </div>
  );

  // Custom Drawer Component
  const CustomizationDrawer = ({ visible, onCancel, onOk, item, sizes, toppings, loading }) => {
    if (!item) return null;
    
    return (
      <Drawer
        title={
          <div className="drawer-header">
            <div className="drawer-title-content">
              <Title level={4} className="drawer-item-title">
                Customize {item.food_item_name_en}
              </Title>
            </div>
           
          </div>
        }
        placement="right"
        onClose={onCancel}
        open={visible}
        width={isMobile ? "100%" : 480}
        className="customization-drawer"
        closable={false}
        extra={
          <Button type="text" onClick={onCancel}>
            <CloseOutlined />
          </Button>
        }
      >
        {loading ? (
          <div className="drawer-loading">
            <Spin size="large" />
            <Text>Loading customizations...</Text>
          </div>
        ) : (
          <div className="drawer-content">
            {item.food_item_img && (
              <div className="drawer-item-image-container">
                <img
                  src={item.food_item_img}
                  alt={item.food_item_name_en}
                  className="drawer-item-image"
                />
              </div>
            )}
            
            {item.food_item_desc_en && (
              <Text type="secondary" className="drawer-item-description">
                {item.food_item_desc_en}
              </Text>
            )}

            {/* Sizes Section */}
            {sizes.length > 0 && (
              <div className="drawer-section">
                <Divider orientation="left" className="section-divider">
                  <MenuOutlined className="section-icon" />
                  <span className="section-title">Select Size</span>
                </Divider>
                <Radio.Group
                  onChange={(e) => setSelectedSize(sizes.find(s => s.id === e.target.value))}
                  value={selectedSize?.id}
                  className="size-options"
                >
                  {sizes.map((size) => (
                    <div 
                      key={size.id} 
                      className={`size-option ${selectedSize?.id === size.id ? 'selected' : ''}`}
                      onClick={() => setSelectedSize(size)}
                    >
                      <Radio value={size.id} className="size-radio">
                        <div className="size-info">
                          <Text strong>{size.name_en}</Text>
                        </div>
                      </Radio>
                      <div className="size-price">
                        {parseFloat(size.delivery_discount) > 0 ? (
                          <>
                            <Text delete type="secondary" className="original-price">
                              £{parseFloat(size.delivery_price).toFixed(2)}
                            </Text>
                            <Text strong className="discounted-price">
                              £{parseFloat(size.delivery_discount).toFixed(2)}
                            </Text>
                          </>
                        ) : (
                          <Text strong>
                            £{parseFloat(size.delivery_price).toFixed(2)}
                          </Text>
                        )}
                      </div>
                    </div>
                  ))}
                </Radio.Group>
              </div>
            )}

            {/* Toppings Section */}
            {toppings.length > 0 && (
              <div className="drawer-section">
                <Divider orientation="left" className="section-divider">
                  <PlusOutlined className="section-icon" />
                  <span className="section-title">Add Extras</span>
                  <Text type="secondary" className="section-subtitle">(Optional)</Text>
                </Divider>
                <div className="topping-options">
                  {toppings.map((topping) => (
                    <div 
                      key={topping.id} 
                      className={`topping-option ${selectedToppings.includes(topping.id) ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedToppings(prev =>
                          prev.includes(topping.id)
                            ? prev.filter(id => id !== topping.id)
                            : [...prev, topping.id]
                        );
                      }}
                    >
                      <Checkbox 
                        checked={selectedToppings.includes(topping.id)} 
                        className="topping-checkbox"
                      >
                        <div className="topping-info">
                          <Text>{topping.name_en}</Text>
                        </div>
                      </Checkbox>
                      <div className="topping-price">
                        <Text strong>+£{parseFloat(topping.delivery_price).toFixed(2)}</Text>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Section */}
            <div className="drawer-summary">
              <Divider className="summary-divider" />
              <div className="summary-row">
                <Text strong>Subtotal:</Text>
                <Text strong className="total-price">£{calculateTotalPrice()}</Text>
              </div>
              
              <Button
                type="primary"
                size="large"
                onClick={onOk}
                className="add-to-order-btn"
                icon={<ShoppingCartOutlined />}
                block
              >
                Add to Order - £{calculateTotalPrice()}
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    );
  };

  if (Menuloading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
        <Text>Loading menu...</Text>
      </div>
    );
  }

  return (
    <div className="food-menu-container">
      {/* Categories Navigation */}
      <div className="categories-nav-container">
        <div className="categories-scroll-wrapper">
          <Button 
            className="scroll-btn left" 
            icon={<LeftOutlined />} 
            onClick={() => scrollCategories('left')}
          />
          
          <div className="categories-scroll-container" ref={categoriesScrollRef}>
            <div className="categories-scroll-content">
              {menuData.map((category) => (
                <Button
                  key={category.id}
                  className={`category-btn ${
                    selectedCategory === category.id ? "active" : ""
                  }`}
                  onClick={() => handleCategoryClick(category.id)}
                >
                  {category.menu_cat_name_en}
                </Button>
              ))}
            </div>
          </div>
          
          <Button 
            className="scroll-btn right" 
            icon={<RightOutlined />} 
            onClick={() => scrollCategories('right')}
          />
        </div>
      </div>

      {/* Food Items */}
      <div className="food-container">
        {menuData.map((category, index) => (
          <div
            key={category.id}
            id={category.id}
            ref={(el) => (sectionRefs.current[index] = el)}
            className="category-section"
          >
            <Title level={2} className="category-title">
              {category.menu_cat_name_en}
            </Title>
            <Row gutter={[16, 16]} className="food-item-grid">
              {category.food_items?.map((item) => {
                const hasDiscount = item.delivery_item_discount_price > 0 || 
                                  item.pickup_item_discount_price > 0;
                
                return (
                  <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                    <Card 
                      className="food-item-card"
                      cover={
                        item.food_item_img && (
                          <div className="food-item-image-container">
                            <img
                              alt={item.food_item_name_en}
                              src={item.food_item_img}
                              className="food-item-image"
                            />
                            {hasDiscount && (
                              <div className="discount-badge">
                                <FireOutlined />
                                <span>Sale</span>
                              </div>
                            )}
                            {item.food_rating > 0 && (
                              <div className="rating-badge">
                                <StarFilled />
                                <span>{parseFloat(item.food_rating).toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                        )
                      }
                    >
                      <div className="food-item-content">
                        <Title level={4} className="food-item-title">
                          {item.food_item_name_en}
                        </Title>
                        
                        <Text type="secondary" className="food-item-description">
                          {item.food_item_desc_en || "Delicious food item"}
                        </Text>

                        <div className="food-item-pricing">
                          {/* Direct Order Prices */}
                          <div className="price-section">
                            <div className="price-row direct-order">
                              <div className="price-label">
                                <span className="platform-name">Direct Order</span>
                                <Tooltip title="Order directly from us for the best price">
                                  <InfoCircleOutlined className="price-info-icon" />
                                </Tooltip>
                              </div>
                              <div className="price-value">
                                {item.delivery_item_price && (
                                  <div className="price-amount">
                                    {item.delivery_item_discount_price > 0 ? (
                                      <>
                                        <Text delete type="secondary" className="original-price">
                                          £{parseFloat(item.delivery_item_price).toFixed(2)}
                                        </Text>
                                        <Text strong className="discounted-price">
                                          £{(
                                            parseFloat(item.delivery_item_price) -
                                            (parseFloat(item.delivery_item_price) *
                                              parseFloat(item.delivery_item_discount_price)) / 100
                                          ).toFixed(2)}
                                        </Text>
                                      </>
                                    ) : (
                                      <Text strong>
                                        £{parseFloat(item.delivery_item_price).toFixed(2)}
                                      </Text>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Platform Comparison */}
                          {comprisionwebs.length > 0 && (
                            <div className="platform-comparison">
                              <div className="comparison-header">
                                <Text type="secondary">Other platforms:</Text>
                              </div>
                              {comprisionwebs.map((platform, idx) => {
                                const discountRate = parseFloat(platform.price_discount) / 100 || 0;
                                const basePrice = parseFloat(item.delivery_item_price) || 0;
                                const platformPrice = basePrice + (basePrice * discountRate);
                                const savings = platformPrice - basePrice;
                                
                                return (
                                  <div key={idx} className="platform-price-row">
                                    <div className="platform-name" style={{ color: platform.platform_color }}>
                                      {platform.company_name}:
                                    </div>
                                    <div className="platform-price">
                                      <Text strong style={{ color: platform.platform_color }}>
                                        £{platformPrice.toFixed(2)}
                                      </Text>
                                      <Text type="secondary" className="platform-discount">
                                        (+{platform.price_discount}%)
                                      </Text>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="food-item-actions">
  {item.size_count > 0 ? (
    <Button
      type="primary"
      onClick={() => handleCustomize(item)}
      className="customize-btn"
      block
      disabled={isRestaurantMenuClosed}
    >
      {isRestaurantMenuClosed ? "Restaurant Closed" : "Customize"}
    </Button>
  ) : (
    <Button
      type="primary"
      className="add-to-cart-btn"
      icon={
        loadingItems[item.id]
          ? <Spin size="small" />
          : <ShoppingCartOutlined />
      }
      onClick={() => handleAddToCart(item, UberEatsComparisonRate)}
      disabled={loadingItems[item.id] || isRestaurantMenuClosed}
      block
    >
      {
        isRestaurantMenuClosed
          ? "Restaurant Closed"
          : loadingItems[item.id]
          ? "Adding..."
          : "Add to Cart"
      }
    </Button>
  )}
</div>

                      {/*   <div className="food-item-actions">
                          {item.size_count > 0 ? (
                            <Button 
                              type="primary" 
                              onClick={() => handleCustomize(item)} 
                              className="customize-btn"
                              block
                              disabled={isRestaurantMenuClosed}
                            >
                              Customize
                            </Button>
                          ) : (
                            <Button
                              type="primary"
                              className="add-to-cart-btn"
                              icon={loadingItems[item.id] ? <Spin size="small" /> : <ShoppingCartOutlined />}
                              onClick={() => handleAddToCart(item, UberEatsComparisonRate)}
                              disabled={loadingItems[item.id]}
                              block
                            >
                              {loadingItems[item.id] ? "Adding..." : "Add to Cart"}
                            </Button>
                          )}
                        </div> */}
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </div>

      {/* Customization Drawer */}
      <CustomizationDrawer
        visible={isDrawerVisible}
        onCancel={handleCancel}
        onOk={handleOk}
        item={selectedItem}
        sizes={sizes}
        toppings={toppings}
        loading={loading}
      />
    </div>
  );
};

export default FoodItems;